import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerDto } from '@/types/players';

interface UseDuelsSearchLogicProps {
  gameweekId?: string;
  onFiltersChange?: (searchQuery: string, playerA: PlayerDto | null, playerB: PlayerDto | null) => void;
  onReset?: () => void;
  selectedPlayerA?: PlayerDto | null;
  selectedPlayerB?: PlayerDto | null;
  resetTrigger?: number;
}

export function useDuelsSearchLogic({ onFiltersChange, onReset, selectedPlayerA, selectedPlayerB, resetTrigger }: UseDuelsSearchLogicProps) {
  const [playerA, setPlayerA] = useState<PlayerDto | null>(null);
  const [playerB, setPlayerB] = useState<PlayerDto | null>(null);

  // sync external props - обновляем внутренний state когда меняются props
  // используем comparison чтобы избежать infinite loops
  useEffect(() => {
    if (selectedPlayerA !== undefined &&
      (selectedPlayerA?.id !== playerA?.id || selectedPlayerA?.web_name !== playerA?.web_name)) {
      setPlayerA(selectedPlayerA || null);
    }
  }, [selectedPlayerA, playerA?.id, playerA?.web_name]);

  useEffect(() => {
    if (selectedPlayerB !== undefined &&
      (selectedPlayerB?.id !== playerB?.id || selectedPlayerB?.web_name !== playerB?.web_name)) {
      setPlayerB(selectedPlayerB || null);
    }
  }, [selectedPlayerB, playerB?.id, playerB?.web_name]);

  const searchQuery = useMemo(() => [playerA?.web_name, playerB?.web_name].filter(Boolean).join(' vs '), [playerA?.web_name, playerB?.web_name]);

  // notify parent
  const notifyRef = useRef(onFiltersChange);
  notifyRef.current = onFiltersChange;
  useEffect(() => {
    const id = window.setTimeout(() => notifyRef.current?.(searchQuery, playerA, playerB), 50);
    return () => window.clearTimeout(id);
  }, [searchQuery, playerA, playerB]);

  const handlePlayerAChange = useCallback((p: PlayerDto | null) => setPlayerA(p), []);
  const handlePlayerBChange = useCallback((p: PlayerDto | null) => setPlayerB(p), []);

  const handleReset = useCallback(() => {
    setPlayerA(null);
    setPlayerB(null);
    onReset?.();
  }, [onReset]);

  // External reset trigger
  useEffect(() => {
    if (resetTrigger !== undefined) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger]);

  const isSelfDuel = useMemo(() => !!(playerA && playerB && playerA.fpl_id === playerB.fpl_id), [playerA, playerB]);

  return { playerA, playerB, isSelfDuel, handlePlayerAChange, handlePlayerBChange, handleReset };
}

